import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { useToast } from "../../hooks/use-toast";
import { cn } from "../../lib/utils";
import { GraduationCap, Loader2, UserPlus, Trash2, CheckCircle2, Clock, CircleDashed, ChevronLeft, Upload } from "lucide-react";

function UploadCourseModal({ open, onClose, onUploaded }) {
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("General");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!title.trim() || !file) { toast({ title: "Title and a .zip file are required", variant: "destructive" }); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("title", title);
      form.append("description", description);
      form.append("category", category);
      form.append("file", file);
      await api.post("/compliance/training/courses", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast({ title: `"${title}" uploaded` });
      setTitle(""); setDescription(""); setCategory("General"); setFile(null);
      onUploaded?.();
    } catch (e) {
      toast({ title: "Couldn't upload course", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="compliance-training-upload-modal">
        <DialogHeader>
          <DialogTitle>Upload a SCORM course</DialogTitle>
          <DialogDescription>Upload a SCORM 1.2 package (.zip with an imsmanifest.xml inside).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" autoFocus data-testid="compliance-training-upload-title-input" />
          </div>
          <div>
            <Label>Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1" data-testid="compliance-training-upload-description-input" />
          </div>
          <div>
            <Label>Category</Label>
            <Input value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1" placeholder="e.g. Security, Privacy, HIPAA" data-testid="compliance-training-upload-category-input" />
          </div>
          <div>
            <Label>SCORM package (.zip)</Label>
            <Input type="file" accept=".zip" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1" data-testid="compliance-training-upload-file-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="compliance-training-upload-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy} className="gap-1.5" data-testid="compliance-training-upload-save-btn">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {busy ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_META = {
  completed: { label: "Completed", icon: CheckCircle2, cls: "text-emerald-600 border-emerald-500/40 bg-emerald-500/10" },
  in_progress: { label: "In progress", icon: Clock, cls: "text-amber-600 border-amber-500/40 bg-amber-500/10" },
  not_started: { label: "Not started", icon: CircleDashed, cls: "text-muted-foreground border-border bg-muted/30" },
};

function AssignModal({ course, open, onClose, onSaved }) {
  const { toast } = useToast();
  const [people, setPeople] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [dueDate, setDueDate] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/compliance/people").then((r) => setPeople(r.data.people || []));
      setSelected(new Set()); setSearch(""); setDueDate("");
    }
  }, [open]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = async () => {
    if (selected.size === 0) { toast({ title: "Pick at least one person", variant: "destructive" }); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/compliance/training/assignments", {
        course_id: course.id, person_ids: Array.from(selected), due_date: dueDate || null,
      });
      const n = data.created?.length || 0;
      const skippedMsg = data.skipped_duplicate ? ` (${data.skipped_duplicate} already assigned)` : "";
      toast({ title: `Assigned to ${n} ${n === 1 ? "person" : "people"}${skippedMsg}` });
      onSaved?.();
    } catch (e) {
      toast({ title: "Couldn't assign course", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const filtered = people.filter((p) => !search || `${p.name} ${p.email}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="compliance-training-assign-modal">
        <DialogHeader>
          <DialogTitle>Assign "{course?.title}"</DialogTitle>
          <DialogDescription>Pick one or more people from the roster.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search people…" data-testid="compliance-training-assign-search" />
          <div className="max-h-56 overflow-y-auto rounded-md border border-border divide-y divide-border">
            {filtered.map((p) => (
              <label key={p.id} className="flex items-center gap-2.5 px-3 py-2 text-[12.5px] cursor-pointer hover:bg-muted/40" data-testid={`compliance-training-assign-person-${p.id}`}>
                <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggle(p.id)} />
                <span className="truncate">{p.name}</span>
                <span className="text-muted-foreground truncate ml-auto">{p.email}</span>
              </label>
            ))}
            {filtered.length === 0 && <div className="px-3 py-4 text-[12px] text-muted-foreground text-center">No matches.</div>}
          </div>
          <div>
            <Label>Due date (optional)</Label>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1" data-testid="compliance-training-assign-due-date" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="compliance-training-assign-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy} className="gap-1.5" data-testid="compliance-training-assign-save-btn">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {busy ? "Assigning…" : `Assign (${selected.size})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CourseDetail({ course, onBack, onChanged }) {
  const { toast } = useToast();
  const [assignments, setAssignments] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const load = useCallback(() => {
    api.get(`/compliance/training/courses/${course.id}/assignments`).then((r) => setAssignments(r.data.assignments || []));
  }, [course.id]);
  useEffect(() => { load(); }, [load]);

  const unassign = async (a) => {
    setRemovingId(a.id);
    try {
      await api.delete(`/compliance/training/assignments/${a.id}`);
      setAssignments((prev) => prev.filter((x) => x.id !== a.id));
      onChanged?.();
    } catch (e) {
      toast({ title: "Couldn't unassign", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setRemovingId(null); }
  };

  return (
    <div className="space-y-3" data-testid="compliance-training-course-detail">
      <button type="button" onClick={onBack} className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground hover:text-foreground" data-testid="compliance-training-back-btn">
        <ChevronLeft className="w-3.5 h-3.5" /> Back to courses
      </button>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[15px] font-semibold">{course.title}</div>
          <div className="text-[12px] text-muted-foreground">{course.description}</div>
        </div>
        <Button size="sm" className="gap-1.5" onClick={() => setAssignOpen(true)} data-testid="compliance-training-assign-btn">
          <UserPlus className="w-3.5 h-3.5" /> Assign
        </Button>
      </div>
      <div className="rounded-xl border border-border bg-card divide-y divide-border">
        {assignments === null && <div className="h-24 bg-muted/40 animate-pulse" />}
        {assignments?.map((a) => {
          const meta = STATUS_META[a.status] || STATUS_META.not_started;
          const Icon = meta.icon;
          return (
            <div key={a.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`compliance-training-assignment-row-${a.id}`}>
              <div className="min-w-0">
                <div className="text-[12.5px] font-medium truncate">{a.person_name}</div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {a.person_email}{a.due_date ? ` · due ${a.due_date}` : ""}{a.score != null ? ` · score ${a.score}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={cn("inline-flex items-center gap-1 text-[10px] font-semibold uppercase rounded-full border px-2 py-0.5", meta.cls)}>
                  <Icon className="w-3 h-3" /> {meta.label}
                </span>
                <button
                  type="button" onClick={() => unassign(a)} disabled={removingId === a.id}
                  className="text-muted-foreground hover:text-red-500 transition-colors"
                  data-testid={`compliance-training-unassign-btn-${a.id}`}
                >
                  {removingId === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          );
        })}
        {assignments?.length === 0 && <div className="text-[11.5px] text-muted-foreground text-center py-8">Not assigned to anyone yet.</div>}
      </div>
      <AssignModal course={course} open={assignOpen} onClose={() => setAssignOpen(false)} onSaved={() => { setAssignOpen(false); load(); onChanged?.(); }} />
    </div>
  );
}

export default function ComplianceTraining() {
  const { toast } = useToast();
  const [courses, setCourses] = useState(null);
  const [activeCourse, setActiveCourse] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(() => {
    api.get("/compliance/training/courses").then((r) => setCourses(r.data.courses || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  const removeCourse = async (e, course) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${course.title}" and all its assignments? This can't be undone.`)) return;
    setDeletingId(course.id);
    try {
      await api.delete(`/compliance/training/courses/${course.id}`);
      setCourses((prev) => prev.filter((c) => c.id !== course.id));
    } catch (err) {
      toast({ title: "Couldn't delete course", description: err?.response?.data?.detail || "", variant: "destructive" });
    } finally { setDeletingId(null); }
  };

  if (!courses) return <div className="h-64 rounded-xl bg-muted/40 animate-pulse" />;

  if (activeCourse) {
    const fresh = courses.find((c) => c.id === activeCourse.id) || activeCourse;
    return <CourseDetail course={fresh} onBack={() => setActiveCourse(null)} onChanged={load} />;
  }

  return (
    <div className="space-y-4" data-testid="compliance-training-page">
      <div className="flex justify-end">
        <Button size="sm" className="gap-1.5" onClick={() => setUploadOpen(true)} data-testid="compliance-training-upload-btn">
          <Upload className="w-3.5 h-3.5" /> Upload course
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {courses.map((c) => (
          <div
            key={c.id}
            role="button"
            tabIndex={0}
            onClick={() => setActiveCourse(c)}
            className="text-left rounded-xl border border-border bg-card p-4 hover:border-primary/40 hover:shadow-sm transition-all cursor-pointer relative"
            data-testid={`compliance-training-course-card-${c.id}`}
          >
            <button
              type="button"
              onClick={(e) => removeCourse(e, c)}
              disabled={deletingId === c.id}
              className="absolute top-3 right-3 text-muted-foreground hover:text-red-500 transition-colors"
              data-testid={`compliance-training-delete-course-btn-${c.id}`}
            >
              {deletingId === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            </button>
            <div className="flex items-center gap-2.5 mb-2">
              <GraduationCap className="w-4 h-4 text-muted-foreground" />
              <span className="text-[10px] font-semibold uppercase tracking-wide rounded-full border border-border px-2 py-0.5 text-muted-foreground">{c.category}</span>
            </div>
            <div className="text-[13.5px] font-semibold pr-6">{c.title}</div>
            <div className="text-[11.5px] text-muted-foreground mt-1 line-clamp-2">{c.description}</div>
            <div className="mt-3 pt-3 border-t border-border/60 flex items-center gap-4 text-[11.5px] text-muted-foreground">
              <span>{c.assigned_count} assigned</span>
              <span className="text-emerald-600">{c.completed_count} completed</span>
              {c.in_progress_count > 0 && <span className="text-amber-600">{c.in_progress_count} in progress</span>}
            </div>
          </div>
        ))}
        {courses.length === 0 && <div className="text-[11.5px] text-muted-foreground text-center py-8 col-span-2">No courses yet.</div>}
      </div>
      <UploadCourseModal open={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={() => { setUploadOpen(false); load(); }} />
    </div>
  );
}
